import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar, Clock, User, ChevronRight, Share2, Copy, Check,
  Facebook, Linkedin, Twitter, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { getArticle, SITE_URL } from "../../articles";
import { useSeo } from "../../lib/useSeo";

const SectionH2 = ({ id, children }) => (
  <h2 id={id} className="font-display font-bold tracking-tight text-2xl md:text-3xl mt-12 mb-5 scroll-mt-24" style={{ color: "var(--navy)" }}>
    {children}
  </h2>
);
const SectionH3 = ({ children }) => (
  <h3 className="font-display font-semibold text-lg md:text-xl mt-8 mb-3" style={{ color: "var(--navy)" }}>
    {children}
  </h3>
);
const P = ({ children }) => (
  <p className="font-body text-base md:text-[17px] leading-[1.8] mt-4" style={{ color: "var(--text2)" }}>{children}</p>
);
const UL = ({ items }) => (
  <ul className="mt-5 space-y-2 font-body text-base md:text-[17px] leading-[1.7]" style={{ color: "var(--text2)" }}>
    {items.map((t, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "var(--blue)" }} />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const ShareButtons = ({ url, title }) => {
  const [copied, setCopied] = React.useState(false);
  const enc = encodeURIComponent;
  const links = {
    wa: `https://wa.me/?text=${enc(title + " " + url)}`,
    fb: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    tw: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`,
    li: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Could not copy"); }
  };
  const btn = "inline-flex h-10 w-10 items-center justify-center rounded-full border transition-all hover:-translate-y-0.5";
  const st = { borderColor: "var(--grey)", background: "#fff", color: "var(--navy)" };
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="article-share">
      <span className="inline-flex items-center gap-2 font-body text-sm font-semibold" style={{ color: "var(--navy)" }}>
        <Share2 size={16} /> Share
      </span>
      <a href={links.wa} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" className={btn} style={st}><MessageCircle size={16} /></a>
      <a href={links.fb} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className={btn} style={st}><Facebook size={16} /></a>
      <a href={links.tw} target="_blank" rel="noopener noreferrer" aria-label="Share on X" className={btn} style={st}><Twitter size={16} /></a>
      <a href={links.li} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className={btn} style={st}><Linkedin size={16} /></a>
      <button onClick={copy} aria-label="Copy link" className={btn} style={st} data-testid="article-share-copy">
        {copied ? <Check size={16} color="var(--green)" /> : <Copy size={16} />}
      </button>
    </div>
  );
};

const ArticleBody = ({ slug }) => {
  if (slug !== "government-job-opportunities-karnataka-youth") return null;
  return (
    <>
      <P>
        Government employment continues to be one of the most respected and trusted career choices for young people
        across Karnataka. Every year, thousands of recruitment opportunities are announced by both the Government of
        Karnataka and the Government of India, offering stable careers, financial security, professional growth, and
        the opportunity to serve society.
      </P>
      <P>
        With increasing competition, success requires more than ambition. Students must prepare with dedication,
        discipline, continuous learning, and the right guidance. By understanding recruitment processes, following
        official notifications, and developing practical skills, Karnataka&apos;s youth can confidently build successful
        careers in public service.
      </P>

      <SectionH2 id="why-government-jobs">Why Government Jobs Continue to Inspire Karnataka&apos;s Youth</SectionH2>
      <P>
        Government jobs provide long-term career stability, transparent recruitment processes, attractive salaries,
        retirement benefits, healthcare facilities, and opportunities to contribute to nation-building. They also allow
        individuals to directly participate in public administration, education, healthcare, law enforcement,
        infrastructure development, and community welfare.
      </P>
      <P>
        For many families across Karnataka, government service represents not only career success but also a chance to
        create a positive impact on society.
      </P>

      <SectionH2 id="popular-opportunities">Popular Government Career Opportunities</SectionH2>
      <P>Students can explore numerous career opportunities through various government recruitment agencies and departments, including:</P>
      <UL items={[
        "Karnataka Public Service Commission (KPSC)",
        "Union Public Service Commission (UPSC)",
        "Staff Selection Commission (SSC)",
        "Institute of Banking Personnel Selection (IBPS)",
        "Reserve Bank of India (RBI)",
        "Indian Railways",
        "Karnataka Police Department",
        "Defence Services (Army, Navy, Air Force)",
        "Public Sector Undertakings (PSUs)",
        "Karnataka Education Department",
        "Health and Medical Services",
        "Forest Department",
        "Revenue Department",
        "Local Government and Municipal Administration",
      ]} />
      <P>Each sector provides opportunities for candidates with different educational backgrounds, qualifications, and interests.</P>

      <SectionH2 id="essential-skills">Essential Skills Every Student Should Develop</SectionH2>
      <P>
        Academic knowledge alone is not enough in today&apos;s competitive environment. Students should continuously
        develop practical skills that improve examination performance and long-term career growth.
      </P>
      <P>Important skills include:</P>
      <UL items={[
        "Effective Communication Skills", "Logical Reasoning", "Quantitative Aptitude", "General Knowledge",
        "Current Affairs Awareness", "Computer Literacy", "English Language Proficiency", "Kannada Language Skills",
        "Time Management", "Problem Solving", "Leadership", "Teamwork", "Decision Making",
      ]} />
      <P>Developing these skills increases confidence during competitive examinations, interviews, and future employment.</P>

      <SectionH2 id="preparation-strategy">Smart Preparation Strategy for Competitive Exams</SectionH2>
      <P>Success in competitive examinations comes from consistency rather than last-minute preparation.</P>
      <P>Students are encouraged to:</P>
      <UL items={[
        "Create a realistic daily study schedule.",
        "Follow the official examination syllabus.",
        "Solve previous years' question papers.",
        "Take regular mock tests.",
        "Read newspapers and reliable current affairs sources.",
        "Practice aptitude and reasoning daily.",
        "Revise important subjects regularly.",
        "Maintain physical and mental well-being.",
        "Stay motivated and avoid unnecessary distractions.",
      ]} />
      <P>Small daily improvements often lead to significant long-term success.</P>

      <SectionH2 id="skill-development">Importance of Skill Development</SectionH2>
      <P>Today&apos;s employment opportunities require both academic excellence and practical knowledge.</P>
      <P>Students should invest time in learning:</P>
      <UL items={[
        "Digital Literacy", "Computer Applications", "Artificial Intelligence Fundamentals",
        "Data Analysis Basics", "Public Speaking", "Professional Communication",
        "Leadership Development", "Financial Literacy", "Entrepreneurship Skills",
      ]} />
      <P>Continuous learning prepares students for both government and private sector careers while improving adaptability in a rapidly changing world.</P>

      <SectionH2 id="official-notifications">Staying Updated with Official Notifications</SectionH2>
      <P>
        Students should always rely on official recruitment websites for authentic information regarding vacancies,
        eligibility, examination dates, and application procedures.
      </P>
      <P>Following official announcements helps candidates avoid misinformation and prepare according to the latest recruitment guidelines. Regular awareness is one of the most important aspects of successful career planning.</P>

      <SectionH2 id="puttraj-vision">Puttraj&apos;s Vision for Karnataka&apos;s Youth</SectionH2>
      <P>A strong Karnataka is built through educated, skilled, and responsible young citizens.</P>
      <P>
        The <Link to="/about" className="font-medium underline decoration-[color:var(--blue)]/40 underline-offset-4 hover:decoration-[color:var(--blue)]" style={{ color: "var(--blue)" }}>Office of Puttraj</Link> believes that education, career guidance, skill development, innovation, and equal opportunities play a vital role in creating stronger communities and a brighter future for the state. Read more about our{" "}
        <Link to="/vision" className="font-medium underline decoration-[color:var(--blue)]/40 underline-offset-4 hover:decoration-[color:var(--blue)]" style={{ color: "var(--blue)" }}>vision for Karnataka</Link>{" "}
        and the ongoing{" "}
        <Link to="/public-service" className="font-medium underline decoration-[color:var(--blue)]/40 underline-offset-4 hover:decoration-[color:var(--blue)]" style={{ color: "var(--blue)" }}>public-service work</Link>.
      </P>
      <P>
        Young people are encouraged to continue learning, participate in skill development programs, remain informed
        about employment opportunities, and contribute positively to society through dedication, integrity, and public
        service.
      </P>

      <SectionH2 id="faq">Frequently Asked Questions</SectionH2>
      <SectionH3>Which government jobs are most popular in Karnataka?</SectionH3>
      <P>Students commonly prepare for KPSC, UPSC, SSC, Banking, Railways, Police, Defence Services, Public Sector Undertakings (PSUs), Education, Health, Revenue, and Local Government recruitment.</P>
      <SectionH3>How should students prepare for government competitive examinations?</SectionH3>
      <P>Students should follow the official syllabus, prepare a disciplined study schedule, solve previous years&apos; question papers, attempt mock tests regularly, and stay updated with current affairs.</P>
      <SectionH3>Which skills improve career opportunities?</SectionH3>
      <P>Communication, reasoning ability, quantitative aptitude, computer literacy, leadership, English proficiency, Kannada language skills, and time management significantly improve career prospects.</P>
      <SectionH3>Why is skill development important?</SectionH3>
      <P>Skill development improves employability, builds confidence, enhances problem-solving ability, and prepares students for both government and private sector opportunities.</P>

      <SectionH2 id="conclusion">Conclusion</SectionH2>
      <P>
        Government careers continue to offer meaningful opportunities for students who are committed to hard work,
        discipline, and continuous improvement. With proper planning, consistent preparation, and the willingness to
        keep learning, Karnataka&apos;s youth can build successful careers while contributing to the progress of society.
      </P>
      <P>
        The Office of Puttraj remains committed to encouraging education, youth empowerment, community development, and
        career awareness across Karnataka. Students are encouraged to stay informed through official government
        recruitment notifications, continue developing their skills, and{" "}
        <Link to="/contact" className="font-medium underline decoration-[color:var(--blue)]/40 underline-offset-4 hover:decoration-[color:var(--blue)]" style={{ color: "var(--blue)" }}>get in touch with the office</Link>{" "}
        to work with determination toward building a brighter future for themselves and the state.
      </P>
    </>
  );
};

const FAQ_JSONLD = {
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Which government jobs are most popular in Karnataka?",
      acceptedAnswer: { "@type": "Answer", text: "Students commonly prepare for KPSC, UPSC, SSC, Banking, Railways, Police, Defence Services, Public Sector Undertakings (PSUs), Education, Health, Revenue, and Local Government recruitment." } },
    { "@type": "Question", name: "How should students prepare for government competitive examinations?",
      acceptedAnswer: { "@type": "Answer", text: "Students should follow the official syllabus, prepare a disciplined study schedule, solve previous years' question papers, attempt mock tests regularly, and stay updated with current affairs." } },
    { "@type": "Question", name: "Which skills improve career opportunities?",
      acceptedAnswer: { "@type": "Answer", text: "Communication, reasoning ability, quantitative aptitude, computer literacy, leadership, English proficiency, Kannada language skills, and time management significantly improve career prospects." } },
    { "@type": "Question", name: "Why is skill development important?",
      acceptedAnswer: { "@type": "Answer", text: "Skill development improves employability, builds confidence, enhances problem-solving ability, and prepares students for both government and private sector opportunities." } },
  ],
};

export const ArticlePage = () => {
  const { slug } = useParams();
  const a = getArticle(slug);
  if (!a) return <Navigate to="/articles" replace />;

  const url = `${SITE_URL}/articles/${a.slug}`;
  const imageAbs = `${SITE_URL}${a.image}`;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useSeo({
    title: a.seoTitle,
    description: a.metaDescription,
    canonical: url,
    url,
    image: imageAbs,
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `${url}#article`,
          headline: a.title,
          description: a.metaDescription,
          image: [imageAbs],
          datePublished: a.publishDate,
          dateModified: a.publishDate,
          inLanguage: "en-IN",
          keywords: a.tags.join(", "),
          articleSection: a.category,
          mainEntityOfPage: url,
          author: { "@type": "Organization", name: a.author, url: `${SITE_URL}/` },
          publisher: {
            "@type": "Organization", name: "Office of Puttraj",
            logo: { "@type": "ImageObject", url: `${SITE_URL}/photos/portrait.jpg` },
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Articles", item: `${SITE_URL}/articles` },
            { "@type": "ListItem", position: 3, name: a.title, item: url },
          ],
        },
        FAQ_JSONLD,
      ],
    },
  });

  return (
    <article className="pt-28 md:pt-36 pb-24" style={{ background: "#FFFFFF" }} data-testid="article-page">
      <div className="container-x max-w-3xl">
        {/* breadcrumbs */}
        <nav aria-label="Breadcrumb" className="font-body text-xs flex items-center gap-2 flex-wrap" style={{ color: "var(--muted)" }}>
          <Link to="/" className="hover:text-[color:var(--navy)] transition-colors">Home</Link>
          <ChevronRight size={12} />
          <Link to="/articles" className="hover:text-[color:var(--navy)] transition-colors">Articles</Link>
          <ChevronRight size={12} />
          <span style={{ color: "var(--text2)" }} className="line-clamp-1">{a.category}</span>
        </nav>

        <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-6">
          <span className="font-body text-[11px] font-semibold tracking-wider uppercase px-3 py-1 rounded-full inline-block"
            style={{ background: "rgba(21,87,214,0.10)", color: "var(--blue)" }}>{a.category}</span>
          <h1 className="font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl leading-[1.1] mt-5" style={{ color: "var(--navy)" }}>
            {a.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 font-body text-sm" style={{ color: "var(--muted)" }}>
            <span className="inline-flex items-center gap-1.5"><User size={14} /> {a.author}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> <time dateTime={a.publishDate}>{a.publishDateDisplay}</time></span>
            <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {a.readMinutes} min read</span>
          </div>
        </motion.header>

        <figure className="mt-8 rounded-2xl overflow-hidden border" style={{ borderColor: "var(--grey)" }}>
          <img src={a.image} alt={a.imageAlt} width="1200" height="630" loading="eager" fetchpriority="high" decoding="async"
            className="w-full h-auto block" />
        </figure>

        <div className="mt-8 border-y py-5 flex flex-wrap items-center justify-between gap-4" style={{ borderColor: "var(--grey)" }}>
          <ShareButtons url={url} title={a.title} />
        </div>

        <div className="mt-6">
          <ArticleBody slug={a.slug} />
        </div>

        {/* tags */}
        <div className="mt-14 pt-8 border-t" style={{ borderColor: "var(--grey)" }}>
          <p className="eyebrow mb-3" style={{ color: "var(--muted)" }}>Tags</p>
          <div className="flex flex-wrap gap-2">
            {a.tags.map((t) => (
              <span key={t} className="font-body text-xs font-medium px-3 py-1.5 rounded-full border"
                style={{ borderColor: "var(--grey)", background: "var(--softblue)", color: "var(--navy)" }}>{t}</span>
            ))}
          </div>
        </div>

        {/* share again + back link */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <ShareButtons url={url} title={a.title} />
          <Link to="/articles" className="btn-primary inline-flex items-center rounded-full font-body font-semibold px-6 py-3">
            Back to Articles
          </Link>
        </div>
      </div>
    </article>
  );
};
